import React from 'react';
import { useParameterizedQuery, useMutation } from 'react-fetching-library';
import { Box, Card, CardContent, List, ListItem, ListItemText, IconButton, Tooltip, useMediaQuery, Menu, MenuItem, Typography, Stack, Divider } from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { plPL } from '@mui/x-data-grid';
import toast from 'react-hot-toast';
import { useSelector } from 'react-redux';

import { useStaff } from '../../../pages/Staff';
import { useManager } from '../../../pages/Manager';
import PDFPreviewModal from '../../employee/applications/applicationForm/tabs/documents/PDFPreviewModal';
import Page from '../../../components/Page';
import Scrollbar from '../../../components/Scrollbar';
import { workStandardStaffColumns } from '../../../services/column-definitions';

import MoreVertIcon from '@mui/icons-material/MoreVert';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import CheckIcon from '@mui/icons-material/Check';

export default function StaffWorkStandardsGeneric ({ manager, downloadDocumentAction, checkDocumentAction, title, serializerFieldName, showChecked = true }) {
  const { application, setApplication } = manager ? useManager() : useStaff();
  const { employeeId } = useSelector(state => state.app);
  const isMobile = useMediaQuery('(max-width:600px)');
  const { query } = useParameterizedQuery(downloadDocumentAction);
  const { mutate } = useMutation(checkDocumentAction);
  const [previewUrl, setPreviewUrl] = React.useState(null);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [menuRow, setMenuRow] = React.useState(null);

  const handleMenuOpen = (event, row) => {
    setAnchorEl(event.currentTarget);
    setMenuRow(row);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuRow(null);
  };

  const handleDownload = async (row, preview = false) => {
    const { payload, error } = await query(row.id);

    if (!error) {
      if (preview) {
        setPreviewUrl(window.URL.createObjectURL(payload));
      } else {
        const url = window.URL.createObjectURL(payload);
        const a = document.createElement('a');
        document.body.appendChild(a);
        a.style = 'display: none';
        a.href = url;
        a.download = `${row.file_name}`;
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } else {
      toast.error('Nie udało się pobrać szablonu');
    }
  };

  const handleCheckDocument = async row => {
    const { payload, error } = await mutate(row.id);
    if (!error) {
      setApplication(prev => ({
        ...prev,
        [serializerFieldName]: prev[serializerFieldName].map(ws => ws.id === payload.id ? payload : ws)
      }));
    }
  };

  if (!application) {
    return null;
  }

  return (
    <Page title={title}>
      <Card>
        <CardContent>
          {isMobile ? (
            <>
              <List>
                {application[serializerFieldName].map(row => (
                  <ListItem key={row.id} divider>
                    <ListItemText primary={<Typography><b>Dokument:</b> {row.name}</Typography>} secondary={<Stack>
                      <Divider flexItem />
                      <Typography><b>Plik:</b> {row.file_name}</Typography>
                      <Typography><b>Opis:</b> {row.description}</Typography>
                    </Stack>} />
                    <IconButton onClick={event => handleMenuOpen(event, row)}>
                      <MoreVertIcon />
                    </IconButton>
                  </ListItem>
                ))}
              </List>
              <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
                {menuRow?.can_preview && (
                  <MenuItem onClick={() => {
                    handleDownload(menuRow, true);
                    handleMenuClose();
                  }}>
                    <RemoveRedEyeIcon style={{ marginRight: 8 }} /> Pokaż
                  </MenuItem>
                )}
                <MenuItem onClick={() => {
                  handleDownload(menuRow);
                  handleMenuClose();
                }}>
                  <FileDownloadIcon style={{ marginRight: 8 }} /> Pobierz Plik
                </MenuItem>
                {showChecked && handleCheckDocument && (
                  <MenuItem
                    disabled={menuRow?.employee_ids_checked.includes(employeeId)}
                    onClick={() => {
                      handleCheckDocument(menuRow);
                      handleMenuClose();
                    }}
                  >
                    <CheckIcon style={{ marginRight: 8 }} /> {menuRow?.employee_ids_checked.includes(employeeId) ? 'Potwierdzono' : 'Potwierdź'}
                  </MenuItem>
                )}
              </Menu>
            </>
          ) : (
            <Scrollbar>
              <Box sx={{ mt: 2 }}>
                <DataGridPro
                  columns={workStandardStaffColumns(handleDownload, showChecked ? handleCheckDocument : undefined, employeeId)}
                  rows={application[serializerFieldName]}
                  density="compact"
                  disableColumnMenu
                  autoHeight
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  pagination
                  components={{ Toolbar: null }}
                  localeText={plPL.components.MuiDataGrid.defaultProps.localeText}
                  disableSelectionOnClick
                />
              </Box>
            </Scrollbar>
          )}
        </CardContent>
      </Card>
      {previewUrl !== null ? <PDFPreviewModal url={previewUrl} handleClose={() => setPreviewUrl(null)} /> : null}
    </Page>
  );
};
